/**
 * Offline runtime, mounted once while a user is signed in (App.tsx):
 *  - installs the viewer/audio offline sources
 *  - prefetches the persisted core queries (usePrefetchCore)
 *  - on every online start / when the network comes back: core data → manifest sync (prunes what
 *    is no longer visible, re-plans packs, auto-runs updates) → resumes downloads → flushes the outbox
 *  - starts the durable outbox (restores queued RSVP / check-in / comments)
 *  - toasts when a pack finished downloading
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { MeDTO } from '../../shared/dto/auth';
import { useToast } from '../components/toast';
import { useT } from '../i18n';
import { flushOutbox, startOutbox } from '../sync/outbox';
import { packTitle } from './format';
import { bindUser, manifestAge, resumeDownloads, syncManifest } from './manager';
import { nudgeQueue } from './downloader';
import { prefetchCore, usePrefetchCore } from './prefetchCore';
import { installOfflineSources, uninstallOfflineSources } from './sources';
import { getSnapshot, subscribe } from './store';

const RESYNC_AFTER_HIDDEN_MS = 15 * 60_000;

function useCompletionToasts(): void {
  const toast = useToast();
  const t = useT();
  const ref = useRef({ toast, t });
  useEffect(() => {
    ref.current = { toast, t };
  });
  useEffect(() => {
    let active = new Set(getSnapshot().packs.filter((p) => p.status === 'downloading').map((p) => p.row.id));
    return subscribe(() => {
      const snap = getSnapshot();
      for (const p of snap.packs) {
        if (active.has(p.row.id) && p.status === 'complete') {
          ref.current.toast.show(ref.current.t('offline.save_done', { title: packTitle(ref.current.t, p.row) }), { tone: 'ok' });
        }
      }
      // A pack stays "seen downloading" until it completes or is removed.
      const next = new Set<string>();
      for (const p of snap.packs) if (p.status === 'downloading' || (active.has(p.row.id) && p.status !== 'complete')) next.add(p.row.id);
      active = next;
    });
  }, []);
}

export function OfflineRuntime({ me }: { me: MeDTO }) {
  const qc = useQueryClient();
  usePrefetchCore();
  useCompletionToasts();

  useEffect(() => {
    installOfflineSources();
    return () => uninstallOfflineSources();
  }, []);

  // Annotation sync (flush queued layer/annotation edits, periodic pull) for this signed-in user.
  useEffect(() => {
    void import('../annotations/useSheetAnnotations').then((m) => m.startAnnotationSync());
  }, []);

  // Latest user for the long-lived online-start handler below (its effect only re-runs for another
  // user): a stale copy would write an old role/section back into the offline state.
  const meRef = useRef(me);
  useEffect(() => {
    meRef.current = me;
  });

  // A role/section change re-plans packs; the manifest (what this user may keep) changes too.
  const audience = useRef(`${me.role}:${me.section}`);
  useEffect(() => {
    void bindUser(me);
    const next = `${me.role}:${me.section}`;
    if (audience.current !== next) {
      audience.current = next;
      if (navigator.onLine) void syncManifest();
    }
  }, [me]);

  useEffect(() => {
    let alive = true;
    const onlineStart = async () => {
      if (!navigator.onLine) return;
      await bindUser(meRef.current);
      await prefetchCore(qc); // song list + members first: song details and avatars are compared against them
      if (!alive) return;
      await syncManifest();
      if (!alive) return;
      resumeDownloads();
      void flushOutbox();
    };
    startOutbox();
    void onlineStart();
    const onOnline = () => {
      nudgeQueue();
      void onlineStart();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && manifestAge() > RESYNC_AFTER_HIDDEN_MS) void onlineStart();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // Re-run only for another user (role/section changes are picked up by bindUser above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.id, qc]);

  return null;
}
